import { XIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../cn";
import { FOCUS_RING } from "./focus-ring";

// WOS-BUNDLE: a company tag as a chip — the badge's 22 px pill in the tag's own colour (the
// `--tag-{color}` soft fill and its AA text, light and dark), with an optional remove × that
// shows on hover or focus (always on touch). Cards and rows show the chip alone; the task drawer
// gives it `onRemove`.

/** Schema `tagColor`: Work OS's eight colours plus the neutral zinc. */
export type TagChipColor =
  | "zinc"
  | "violet"
  | "sky"
  | "emerald"
  | "amber"
  | "rose"
  | "indigo"
  | "teal"
  | "orange";

export type TagChipProps = Omit<React.ComponentProps<"span">, "color" | "children"> & {
  name: string;
  color: TagChipColor;
  /** Shows a × that takes the tag off (named "Remove tag {name}"). */
  onRemove?: () => void;
  removeLabel?: string;
};

/** The chip's fill and text for a colour (inline: the colour is data, not a class). */
export function tagChipStyle(color: TagChipColor): React.CSSProperties {
  return { backgroundColor: `var(--tag-${color})`, color: `var(--tag-${color}-foreground)` };
}

export function TagChip({
  name,
  color,
  onRemove,
  removeLabel,
  className,
  style,
  ...props
}: TagChipProps) {
  return (
    <span
      data-slot="tag-chip"
      data-color={color}
      className={cn(
        "group/tag inline-flex h-[22px] max-w-48 shrink-0 items-center gap-1 rounded-sm px-2 text-2xs font-ui whitespace-nowrap",
        onRemove && "pr-1",
        className,
      )}
      style={{ ...tagChipStyle(color), ...style }}
      {...props}
    >
      <span className="min-w-0 truncate">{name}</span>
      {onRemove ? (
        <button
          type="button"
          aria-label={removeLabel ?? `Remove tag ${name}`}
          className={cn(
            "-mr-0.5 flex size-4 shrink-0 items-center justify-center rounded-xs opacity-60 hover:bg-current/15 hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100",
            FOCUS_RING,
          )}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <XIcon aria-hidden="true" className="size-3" />
        </button>
      ) : null}
    </span>
  );
}

/** A colour swatch (Settings → Tags, menus): a 20 px dot in the tag's text tone. */
export function TagSwatch({
  color,
  selected,
  className,
  ...props
}: Omit<React.ComponentProps<"span">, "color"> & { color: TagChipColor; selected?: boolean }) {
  return (
    <span
      data-slot="tag-swatch"
      data-selected={selected || undefined}
      className={cn(
        "inline-block size-5 shrink-0 rounded-full border border-current/25 data-selected:ring-2 data-selected:ring-ring data-selected:ring-offset-1 data-selected:ring-offset-popover",
        className,
      )}
      // The text tone as the fill: the soft fills are too alike side by side.
      style={{ backgroundColor: `var(--tag-${color}-foreground)`, color: `var(--tag-${color})` }}
      {...props}
    />
  );
}
